import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { getCart, updateCartItem, removeCartItem } from '../services/cartApi';
import { useCurrency } from '../context/CurrencyContext';

const Cart = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { formatPrice } = useCurrency();
  const [cartItems, setCartItems] = useState([]);

  useEffect(() => {
    const token =
      typeof localStorage !== 'undefined'
        ? localStorage.getItem('token')
        : null;
    if (token) {
      getCart()
        .then((cart) => {
          const mapped = (cart?.items || []).map((it) => ({
            itemId: it._id,
            id: it.product?._id || it.product, // product id
            name: it.product?.name || 'Product',
            image:
              (it.product?.images && it.product.images[0]) ||
              '/assets/product-image.webp',
            price: it.product?.pricing?.regularPrice ?? it.product?.price ?? 0,
            quantity: it.quantity ?? 1,
            color: it.color,
            size: it.size,
          }));
          setCartItems(mapped);
        })
        .catch(() => {
          const stored = JSON.parse(localStorage.getItem('cart') || '[]');
          setCartItems(stored);
        });
    } else {
      const stored = JSON.parse(localStorage.getItem('cart') || '[]');
      setCartItems(stored);
    }
  }, []);

  const [couponCode, setCouponCode] = useState('');
  const [deliveryAddress, setDeliveryAddress] = useState('');
  const [showCouponDropdown, setShowCouponDropdown] = useState(false);
  const [showDeliveryDropdown, setShowDeliveryDropdown] = useState(false);

  const updateQuantity = (id, newQuantity, itemId) => {
    if (newQuantity < 1) return;
    const token =
      typeof localStorage !== 'undefined'
        ? localStorage.getItem('token')
        : null;
    if (token && itemId) {
      updateCartItem(itemId, newQuantity)
        .then(() =>
          getCart().then((cart) => {
            const mapped = (cart?.items || []).map((it) => ({
              itemId: it._id,
              id: it.product?._id || it.product,
              name: it.product?.name || 'Product',
              image:
                (it.product?.images && it.product.images[0]) ||
                '/assets/product-image.webp',
              price:
                it.product?.pricing?.regularPrice ?? it.product?.price ?? 0,
              quantity: it.quantity ?? 1,
              color: it.color,
              size: it.size,
            }));
            setCartItems(mapped);
          })
        )
        .catch(() => {});
    } else {
      setCartItems((items) => {
        const updated = items.map((item) =>
          item.id === id ? { ...item, quantity: newQuantity } : item
        );
        localStorage.setItem('cart', JSON.stringify(updated));
        return updated;
      });
    }
  };

  const removeItem = (id, itemId) => {
    const token =
      typeof localStorage !== 'undefined'
        ? localStorage.getItem('token')
        : null;
    if (token && itemId) {
      removeCartItem(itemId)
        .then(() =>
          getCart().then((cart) => {
            const mapped = (cart?.items || []).map((it) => ({
              itemId: it._id,
              id: it.product?._id || it.product,
              name: it.product?.name || 'Product',
              image:
                (it.product?.images && it.product.images[0]) ||
                '/assets/product-image.webp',
              price:
                it.product?.pricing?.regularPrice ?? it.product?.price ?? 0,
              quantity: it.quantity ?? 1,
              color: it.color,
              size: it.size,
            }));
            setCartItems(mapped);
          })
        )
        .catch(() => {});
    } else {
      setCartItems((items) => {
        const updated = items.filter((item) => item.id !== id);
        localStorage.setItem('cart', JSON.stringify(updated));
        return updated;
      });
    }
  };

  const subtotal = cartItems.reduce(
    (total, item) => total + item.price * item.quantity,
    0
  );
  const delivery = 0;
  const total = subtotal + delivery;

  return (
    <div className="mt-[78px] px-4 sm:px-6 lg:px-8">
      <div className="max-w-[1180px] mx-auto pt-8 sm:pt-[92px] pb-[166px] sm:pb-[100px] flex items-start lg:flex-row flex-col justify-between gap-10 lg:gap-8">
        {/* Left Section - Products */}
        <div className="lg:max-w-[684px] w-full">
          <div className="flex items-center justify-between gap-6 mb-6">
            <h3 className="text-black text-[16px] leading-[22px] font-semibold">
              {t('cart.product')}
            </h3>
            <h3 className="text-black text-[16px] leading-[22px] font-semibold">
              {t('cart.total')}
            </h3>
          </div>

          {cartItems.length > 0 ? (
            cartItems.map((item) => (
              <div
                key={item.id}
                className="w-full border-y border-[#AFAFAF] py-6"
              >
                <div className="max-w-[684px] w-full flex items-start justify-between gap-2">
                  <div className="flex items-center gap-4">
                    <div className="w-[119px] h-[119px] bg-[#D9D9D9] flex items-center justify-center overflow-hidden p-1 rounded">
                      <img
                        src={item.image}
                        alt={item.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="space-y-4">
                      <h2 className="text-black text-[20px] leading-[27px] font-semibold">
                        {item.name}
                      </h2>
                      <div className="border border-[#AFAFAF] rounded-[2px] p-2 flex items-center justify-between gap-[30px]">
                        <button
                          onClick={() =>
                            updateQuantity(
                              item.id,
                              item.quantity - 1,
                              item.itemId
                            )
                          }
                          className="text-black text-[18px] leading-[25px] font-medium hover:bg-gray-100 rounded"
                        >
                          <svg
                            width="6"
                            height="2"
                            viewBox="0 0 6 2"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M0.666 1.986V0.366H5.13V1.986H0.666Z"
                              fill="black"
                            />
                          </svg>
                        </button>
                        <span className="text-black text-[18px] leading-[25px] font-medium">
                          {item.quantity}
                        </span>
                        <button
                          onClick={() =>
                            updateQuantity(
                              item.id,
                              item.quantity + 1,
                              item.itemId
                            )
                          }
                          className="text-black text-[18px] leading-[25px] font-medium hover:bg-gray-100 rounded"
                        >
                          <svg
                            width="10"
                            height="9"
                            viewBox="0 0 10 9"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M5.868 3.926H9.396V5.384H5.868V9.002H4.392V5.384H0.864V3.926H4.392V0.289999H5.868V3.926Z"
                              fill="black"
                            />
                          </svg>
                        </button>
                      </div>
                      <button
                        onClick={() => removeItem(item.id, item.itemId)}
                        className="text-black text-[14px] leading-[19px] font-normal underline hover:text-gray-600"
                      >
                        {t('cart.removeItem')}
                      </button>
                    </div>
                  </div>
                  <h2 className="text-black text-[18px] leading-[25px] font-medium">
                    {formatPrice(item.price * item.quantity)}
                  </h2>
                </div>
              </div>
            ))
          ) : (
            <div className="w-full border-y border-[#AFAFAF] py-6 text-center">
              <div className="max-w-[684px] w-full flex flex-col items-center justify-center gap-4">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M7 4V2C7 1.45 7.45 1 8 1H16C16.55 1 17 1.45 17 2V4H20C20.55 4 21 4.45 21 5S20.55 6 20 6H19V19C19 20.1 18.1 21 17 21H7C5.9 21 5 20.1 5 19V6H4C3.45 6 3 5.55 3 5S3.45 4 4 4H7ZM9 3V4H15V3H9ZM7 6V19H17V6H7Z"
                      fill="#9CA3AF"
                    />
                  </svg>
                </div>
                <h3 className="text-gray-600 text-[18px] leading-[25px] font-semibold">
                  {t('cart.empty.title')}
                </h3>
                <p className="text-gray-500 text-[14px] leading-[20px] font-normal max-w-md">
                  {t('cart.empty.subtitle')}
                </p>
                <button
                  className="mt-4 bg-black text-white px-10 py-5 uppercase text-[13px] leading-[14px] font-medium"
                  onClick={() => navigate(`/${i18n.language}`)}
                >
                  {t('cart.empty.cta')}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Right Section - Cart Total */}
        <div className="lg:max-w-[396px] w-full">
          <div className="mb-6">
            <h3 className="text-black text-[16px] leading-[22px] font-semibold">
              {t('cart.summary.title')}
            </h3>
          </div>

          {cartItems.length > 0 ? (
            <>
              {/* Coupon Section */}
              <button className="px-4 w-full flex justify-between items-center flex-col py-[17px] border-y border-[#AFAFAF]">
                <div
                  className="w-full flex items-center justify-between gap-4"
                  onClick={() => setShowCouponDropdown(!showCouponDropdown)}
                >
                  <span className="text-black text-[16px] leading-[22px] font-normal">
                    {t('cart.coupon.add')}
                  </span>
                  <div
                    onClick={() => setShowCouponDropdown(!showCouponDropdown)}
                    className="flex items-center gap-1"
                  >
                    <svg
                      width="12"
                      height="12"
                      viewBox="0 0 12 12"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M5.46967 9.53033C5.76256 9.82322 6.23744 9.82322 6.53033 9.53033L11.3033 4.75736C11.5962 4.46447 11.5962 3.98959 11.3033 3.6967C11.0104 3.40381 10.5355 3.40381 10.2426 3.6967L6 7.93934L1.75736 3.6967C1.46447 3.40381 0.989593 3.40381 0.696699 3.6967C0.403806 3.98959 0.403806 4.46447 0.696699 4.75736L5.46967 9.53033ZM6 8H5.25V9H6H6.75V8H6Z"
                        fill="black"
                      />
                    </svg>
                  </div>
                </div>
                {showCouponDropdown && (
                  <div className="mt-5 w-full">
                    <input
                      type="text"
                      placeholder={t('cart.coupon.placeholder')}
                      value={couponCode}
                      onChange={(e) => setCouponCode(e.target.value)}
                      className="w-full border border-[#AFAFAF] rounded px-3 py-3 text-[13px] outline-none"
                    />
                  </div>
                )}
              </button>

              {/* Subtotal */}
              <div className="flex justify-between items-center mt-6 px-4">
                <span className="text-black text-[16px] leading-[22px] font-normal">
                  {t('cart.subtotal')}
                </span>
                <span className="text-black text-[16px] leading-[22px] font-normal">
                  {formatPrice(subtotal)}
                </span>
              </div>

              {/* Delivery */}
              <div className="px-4 py-[19px]">
                <span className="text-black text-[16px] leading-[22px] font-normal">
                  {t('cart.delivery')}
                </span>
              </div>

              {/* Enter Address to Check Delivery Options */}
              <div className="w-full flex justify-between items-center flex-col px-4 pb-6 border-b border-[#AFAFAF]">
                <div
                  className="w-full flex items-center justify-between gap-4 cursor-pointer"
                  onClick={() => setShowDeliveryDropdown(!showDeliveryDropdown)}
                >
                  <span className="text-black text-[16px] leading-[22px] font-normal">
                    {t('cart.enterAddress')}
                  </span>
                  <button
                    onClick={() =>
                      setShowDeliveryDropdown(!showDeliveryDropdown)
                    }
                    className="flex items-center gap-1"
                  >
                    <svg
                      width="12"
                      height="12"
                      viewBox="0 0 12 12"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M5.46967 9.53033C5.76256 9.82322 6.23744 9.82322 6.53033 9.53033L11.3033 4.75736C11.5962 4.46447 11.5962 3.98959 11.3033 3.6967C11.0104 3.40381 10.5355 3.40381 10.2426 3.6967L6 7.93934L1.75736 3.6967C1.46447 3.40381 0.989593 3.40381 0.696699 3.6967C0.403806 3.98959 0.403806 4.46447 0.696699 4.75736L5.46967 9.53033ZM6 8H5.25V9H6H6.75V8H6Z"
                        fill="black"
                      />
                    </svg>
                  </button>
                </div>

                {showDeliveryDropdown && (
                  <div className="mt-5 w-full">
                    <input
                      type="text"
                      placeholder={t('cart.addressPlaceholder')}
                      value={deliveryAddress}
                      onChange={(e) => setDeliveryAddress(e.target.value)}
                      className="w-full border border-[#AFAFAF] rounded px-3 py-3 text-[13px] outline-none"
                    />
                  </div>
                )}
              </div>

              {/* Total */}
              <div className="px-4 w-full flex justify-between items-center pt-3.5 pb-6">
                <span className="text-black text-[16px] leading-[22px] font-normal">
                  {t('cart.total')}
                </span>
                <span className="text-black text-[16px] leading-[22px] font-normal">
                  {formatPrice(total)}
                </span>
              </div>

              {/* Checkout Button */}
              <button
                className="bg-black w-full h-[54px] text-white text-[13px] leading-[14px] font-medium uppercase"
                onClick={() => navigate(`/${i18n.language}/checkout`)}
              >
                {t('cart.checkoutCta')}
              </button>
            </>
          ) : (
            <div className="text-center py-6 border-y border-[#AFAFAF]">
              <div className="text-[#AFAFAF] text-[16px] leading-[20px] font-normal mb-6">
                {t('cart.emptySummary.help')}
              </div>
              <div className="text-[#AFAFAF] text-[14px] leading-[18px]">
                <div className="flex justify-between items-center">
                  <span>{t('cart.subtotal')}</span>
                  <span>{formatPrice(0)}</span>
                </div>
                <div className="mt-4 flex justify-between items-center">
                  <span>{t('cart.delivery')}</span>
                  <span>{formatPrice(0)}</span>
                </div>
                <div className="mt-6 pt-6 flex justify-between items-center py-1 border-t border-[#AFAFAF]">
                  <span className="font-semibold">{t('cart.total')}</span>
                  <span className="font-semibold">{formatPrice(0)}</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Cart;
